import React from 'react';

import { Autocomplete } from '@mui/material';
import { ArcOutlinedInput } from '@src/core/ui';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import { OptionEntity, GoogleSearchInputProps } from './types';
import { toast } from 'react-toastify';
import { EMPTY_VALUE } from '@src/core/config';

const GoogleSearchInput: React.FC<GoogleSearchInputProps> = (props) => {
  const { readOnly, label, error, helperText, initValue, endAdornment, onSelect } = props;
  const { ready, suggestions: { status, data }, value, setValue, clearSuggestions } = usePlacesAutocomplete({ 
    defaultValue: initValue ?? EMPTY_VALUE 
  });

  const onInputChange = (_: React.ChangeEvent<unknown>, value: string) => setValue(value);

  const getGeoCode = async (address: string) => {
    try {
      const results = await getGeocode({ address });
      const { lat, lng } = await getLatLng(results[0]);
      return { lat, lng };
    } catch (error) {
      toast.error(`[GetGeoCode]: ${error}`);
    }
  };

  const onSelectChange = async (option: OptionEntity | null | string) => {
    if (!option) return;

    const address = typeof option === 'string' ? option : option.value;

    setValue(address);
    clearSuggestions();

    const geo = await getGeoCode(address);

    if (!geo) return;

    onSelect({ latitude: geo.lat, longitude: geo.lng, address });
  };

  const options = status === 'OK' ? data.map(({ description }) => ({
    label: description,
    value: description,
  })) : [];

  return (
    <Autocomplete 
      freeSolo 
      fullWidth 
      disableClearable
      size='small'
      disabled={!ready}
      options={options} 
      value={initValue}
      inputValue={value}
      readOnly={readOnly}
      onInputChange={onInputChange}
      onChange={(_, option) => onSelectChange(option)}
      renderInput={(params) => (
        <ArcOutlinedInput 
          {...params} 
          fullWidth 
          label={label} 
          error={error} 
          disabled={readOnly}
          helperText={helperText}
          slotProps={{ 
            input: {
              ...params.InputProps, 
              endAdornment: (
                <React.Fragment>
                  {params.InputProps.endAdornment}
                  {endAdornment}
                </React.Fragment>
              )
            } 
          }}
        />
      )}/>
  );
};

export default GoogleSearchInput;